<template>
  <div>
    <div class="back" v-if="isVisible" @click="hideModal"></div>
    <div class="noticeView" v-if="isVisible">
      <div class="modalView_pop">
        <div class="modalView_pop_l">
          <div class="modalView_pop_l_view">
            <div class="modalView_pop_l_view_t">
              <div class="modalView_pop_l_view_t_1">
                <div class="modalView_pop_l_view_t_3_text">{{ $t('notice.title') }}</div>
              </div>
              <div class="modalView_pop_l_view_t_2">{{ $t('notice.desc') }}</div>
            </div>
            <div class="modalView_pop_l_view_b">
              <div class="notice-body">
                <!-- 左侧：消息列表 -->
                <div class="notice-list-panel">
                  <div class="notice-toolbar">
                    <el-checkbox
                      v-model="selectAll"
                      :indeterminate="isIndeterminate"
                      @change="handleSelectAll"
                    >
                      {{ $t('notice.selectAll') }}
                    </el-checkbox>
                    <div class="notice-toolbar-actions">
                      <el-button
                        class="notice-toolbar-read-button"
                        :disabled="selectedIds.length === 0"
                        @click="handleBatchRead"
                      >
                        {{ $t('notice.batchRead') }}
                      </el-button>
                      <el-button
                        class="notice-toolbar-delete-button"
                        :disabled="selectedIds.length === 0"
                        @click="handleBatchDelete"
                      >
                        {{ $t('notice.batchDelete') }}
                      </el-button>
                    </div>
                  </div>

                  <div class="notice-list-scroll" v-loading="loading">
                    <div v-if="!loading && messageList.length === 0" class="notice-empty">
                      {{ $t('notice.empty') }}
                    </div>
                    <el-checkbox-group v-else v-model="selectedIds" @change="handleSelectionChange">
                      <div
                        v-for="item in messageList"
                        :key="item.id"
                        class="notice-list-item"
                        :class="{
                          'is-active': activeId === item.id,
                          'is-unread': item.status === 0
                        }"
                        @click="selectMessage(item)"
                      >
                        <el-checkbox :label="item.id" @click.native.stop >&nbsp;</el-checkbox>
                        <div class="notice-list-item-main">
                          <div class="notice-list-item-title">
                            <span v-if="item.status === 0" class="unread-dot"></span>
                            <span class="title-text">{{ item.title }}</span>
                          </div>
                          <div class="notice-list-item-meta">
                            <span class="type-tag" :class="getMessageTypeClass(item.message_type)">{{ getMessageTypeLabel(item.message_type) }}</span>
                            <span class="time-text">{{ formatTime(item.created_at) }}</span>
                          </div>
                        </div>
                      </div>
                    </el-checkbox-group>
                  </div>

                  <div class="notice-pagination" v-if="total > 0">
                    <el-pagination
                      @current-change="handlePageChange"
                      :current-page.sync="currentPage"
                      :page-size="pageSize"
                      layout="prev, pager, next"
                      :total="total"
                    />
                  </div>
                </div>

                <!-- 右侧：消息详情 -->
                <div class="notice-detail-panel">
                  <template v-if="activeMessage">
                    <div class="notice-detail-header">
                      <div class="notice-detail-title">{{ activeMessage.title }}</div>
                      <div class="notice-detail-meta">
                        <span class="type-tag" :class="getMessageTypeClass(activeMessage.message_type)">{{ getMessageTypeLabel(activeMessage.message_type) }}</span>
                        <!-- <span class="status-tag" :class="{ unread: activeMessage.status === 0 }">
                          {{ activeMessage.status === 0 ? $t('notice.unread') : $t('notice.read') }}
                        </span> -->
                        <span class="time-text">{{ formatTime(activeMessage.created_at) }}</span>
                      </div>
                    </div>
                    <div class="notice-detail-content" v-html="activeMessage.content || ''"></div>
                    <!-- <div class="notice-detail-actions">
                      <el-button
                        v-if="activeMessage.status === 0"
                        size="small"
                        type="primary"
                        plain
                        @click="markSingleRead(activeMessage)"
                      >
                        {{ $t('notice.read') }}
                      </el-button>
                      <el-button
                        size="small"
                        type="danger"
                        plain
                        @click="deleteSingle(activeMessage)"
                      >
                        {{ $t('notice.delete') }}
                      </el-button>
                    </div> -->
                  </template>
                  <div v-else class="notice-detail-empty">
                    {{ $t('notice.selectHint') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getNoticeList, readNotice, deleteNotice } from '@/api';
import { fetchNoticeUnreadCount } from '@/utils/noticeService';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      isVisible: false, // 是否显示弹窗
      loading: false, // 是否加载中
      messageList: [], // 消息列表
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页条数
      total: 0, // 总条数
      activeId: null, // 当前选中的消息ID
      selectedIds: [], // 选中消息ID列表
      selectAll: false, // 是否全选
    };
  },
  computed: {
    ...mapState(['userInfo']),
    activeMessage() {
      return this.messageList.find((item) => item.id === this.activeId) || null;
    },
    isIndeterminate() {
      const len = this.selectedIds.length;
      return len > 0 && len < this.messageList.length;
    },
  },
  mounted() {
    // 监听全局事件并刷新列表
    this.$root.$on('notice-list-refresh', this.handleExternalRefresh);
  },
  beforeDestroy() {
    // 销毁全局事件
    this.$root.$off('notice-list-refresh', this.handleExternalRefresh);
  },
  methods: {
    // WebSocket 推送时刷新列表
    handleExternalRefresh() {
      if (this.isVisible) {
        this.fetchMessageList();
      }
    },

    // 显示弹窗
    showModal() {
      this.isVisible = true;
      this.currentPage = 1;
      this.activeId = null;
      this.selectedIds = [];
      this.selectAll = false;
      this.fetchMessageList();
    },

    // 隐藏弹窗
    hideModal() {
      this.isVisible = false;
      this.activeId = null;
      this.selectedIds = [];
      this.selectAll = false;
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '';
      return this.$dayjs(time).format('YYYY-MM-DD HH:mm:ss');
    },

    // 获取消息类型标签
    getMessageTypeLabel(type) {
      const key = `notice.messageType.${type}`;
      const text = this.$t(key);
      return text === key ? this.$t('notice.messageType.other') : text;
    },

    // 获取消息类型标签类
    getMessageTypeClass(type) {
      switch (type) {
        case 'system':
          return 'system-tag';
        case 'notification':
          return 'notification-tag';
        case 'alert':
          return 'alert-tag';
        default:
          return 'other-tag';
      }
    },

    // 获取消息列表
    async fetchMessageList() {
      this.loading = true;
      try {
        const res = await getNoticeList({
          page_no: this.currentPage,
          page_size: this.pageSize,
        });
        const data = res?.data?.data || {};
        this.messageList = Array.isArray(data.items) ? data.items : [];
        this.total = data.total || 0;
        this.syncSelectionAfterFetch();
      } catch {
        this.messageList = [];
        this.total = 0;
        this.$message.error(this.$t('notice.loadError'));
      } finally {
        this.loading = false;
      }
    },

    // 同步选中状态
    syncSelectionAfterFetch() {
      const ids = this.messageList.map((item) => item.id);
      this.selectedIds = this.selectedIds.filter((id) => ids.includes(id));
      if (this.activeId && !ids.includes(this.activeId)) {
        this.activeId = null;
      }
      this.selectAll = this.messageList.length > 0 && this.selectedIds.length === this.messageList.length;
    },

    // 分页切换
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchMessageList();
    },

    // 选中状态变化
    handleSelectionChange() {
      this.selectAll = this.messageList.length > 0 && this.selectedIds.length === this.messageList.length;
    },

    // 全选
    handleSelectAll(val) {
      this.selectedIds = val ? this.messageList.map((item) => item.id) : [];
    },

    // 选择消息
    async selectMessage(item) {
      this.activeId = item.id;
      if (item.status === 0) {
        await this.markSingleRead(item, false);
      }
    },

    // 标记单条消息已读
    async markSingleRead(item, showTip = true) {
      if (!item || item.status === 1) return;
      try {
        await readNotice(item.id, { status: 1 });
        item.status = 1;
        await fetchNoticeUnreadCount();
        if (showTip) {
          this.$message.success(this.$t('notice.batchReadSuccess'));
        }
      } catch {
        this.$message.error(this.$t('notice.loadError'));
      }
    },

    // 批量标记已读
    async handleBatchRead() {
      if (this.selectedIds.length === 0) {
        this.$message.warning(this.$t('notice.noSelection'));
        return;
      }
      const targets = this.messageList.filter(
        (item) => this.selectedIds.includes(item.id) && item.status === 0
      );
      if (targets.length === 0) {
        this.$message.info(this.$t('notice.batchReadSuccess'));
        return;
      }
      try {
        await Promise.all(targets.map((item) => readNotice(item.id, { status: 1 })));
        targets.forEach((item) => {
          item.status = 1;
        });
        await fetchNoticeUnreadCount();
        this.$message.success(this.$t('notice.batchReadSuccess'));
      } catch {
        this.$message.error(this.$t('notice.loadError'));
      }
    },

    // 删除单条消息
    async deleteSingle(item) {
      if (!item) return;
      try {
        await this.$confirm(this.$t('notice.deleteConfirm'), this.$t('notice.confirm'), {
          confirmButtonText: this.$t('notice.confirm'),
          cancelButtonText: this.$t('notice.cancel'),
          type: 'warning',
        });
        await deleteNotice(item.id);
        if (this.activeId === item.id) {
          this.activeId = null;
        }
        this.selectedIds = this.selectedIds.filter((id) => id !== item.id);
        await fetchNoticeUnreadCount();
        this.$message.success(this.$t('notice.batchDeleteSuccess'));
        await this.fetchMessageList();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('notice.loadError'));
        }
      }
    },

    // 批量删除
    async handleBatchDelete() {
      if (this.selectedIds.length === 0) {
        this.$message.warning(this.$t('notice.noSelection'));
        return;
      }
      try {
        await this.$confirm(this.$t('notice.deleteConfirmBatch'), this.$t('notice.confirm'), {
          confirmButtonText: this.$t('notice.confirm'),
          cancelButtonText: this.$t('notice.cancel'),
          type: 'warning',
        });
        await Promise.all(this.selectedIds.map((id) => deleteNotice(id)));
        if (this.selectedIds.includes(this.activeId)) {
          this.activeId = null;
        }
        this.selectedIds = [];
        this.selectAll = false;
        await fetchNoticeUnreadCount();
        this.$message.success(this.$t('notice.batchDeleteSuccess'));
        await this.fetchMessageList();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(this.$t('notice.loadError'));
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.back {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(33, 33, 33, 0.5);
  height: 100vh;
  z-index: 999;
}

.noticeView {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--global-background);
  width: 1200px;
  height: 650px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  z-index: 1001;

  .modalView_pop {
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    background-color: #ffffff;
    background-image: var(--global-inner-background);
    border-radius: 20px;
    display: flex;

    .modalView_pop_l {
      width: 1100px;
      height: 100%;
      position: relative;

      .modalView_pop_l_view {
        width: 100%;
        height: 100%;
        margin: 0 10px 0 35px;
        border-left: 2px dashed #E3E6E8;
        border-right: 2px dashed #E3E6E8;

        .modalView_pop_l_view_t {
          width: 100%;
          border-bottom: 2px dashed #E3E6E8;
          height: 140px;
          display: flex;
          flex-direction: column;
          justify-content: center;

          .modalView_pop_l_view_t_1 {
            width: 223px;
            height: 46px;
            margin-left: 33px;

            .modalView_pop_l_view_t_3_text {
              font-size: 35px;
              color: rgba(33, 33, 33, 1);
              font-weight: 500;
            }
          }

          .modalView_pop_l_view_t_2 {
            font-size: 18px;
            color: rgba(0, 0, 0, 1);
            font-weight: 500;
            margin-left: 35px;
            letter-spacing: 3px;
          }
        }

        .modalView_pop_l_view_b {
          width: 100%;
          height: calc(100% - 160px);
          position: relative;
          padding: 0 20px 16px 10px;
          box-sizing: border-box;
        }
      }
    }
  }
}

.notice-body {
  display: flex;
  height: 100%;
  gap: 16px;
}

.notice-list-panel {
  width: 42%;
  min-width: 360px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #E3E6E8;
  padding-right: 12px;
}

.notice-toolbar {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px;
  border-bottom: 1px solid #EEF1F3;
}

.notice-toolbar-actions {
  display: flex;
  gap: 8px;
}

.notice-toolbar-read-button {
  background: rgb(24, 161, 246);
  color: #fff;
  border-radius: 15px;

  &:hover {
    background: rgb(24, 161, 246, 0.8);
    color: #fff;
    border-radius: 15px;
  }
}

.notice-toolbar-delete-button {
  background: rgb(255, 65, 65);
  color: #fff;
  border-radius: 15px;

  &:hover {
    background: rgb(255, 65, 65, 0.8);
    color: #fff;
    border-radius: 15px;
  }
}

.notice-list-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  // 滚动条样式
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #E3E6E8;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #F5F5F5;
    border-radius: 3px;
  }
}

.notice-empty,
.notice-detail-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 14px;
}

.notice-detail-empty {
  margin-top: 60px;
}

.notice-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover,
  &.is-active {
    background: rgba(86, 198, 151, 0.08);
  }

  &.is-unread .title-text {
    font-weight: 600;
  }
}

.notice-list-item-main {
  flex: 1;
  min-width: 0;
}

.notice-list-item-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;

  .title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #212121;
    font-size: 14px;
  }
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #F56C6C;
  flex-shrink: 0;
}

.notice-list-item-meta,
.notice-detail-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.type-tag {
  font-size: 12px;
  color: #56C697;
  background: rgba(86, 198, 151, 0.12);
  padding: 2px 8px;
  border-radius: 10px;
}

.system-tag {
  color: #56C697;
  background: rgba(86, 198, 151, 0.12);
}

.notification-tag {
  color: #FFA500;
  background: rgba(255, 165, 0, 0.12);
}

.alert-tag {
  color: #FF0000;
  background: rgba(255, 0, 0, 0.12);
}

.other-tag {
  color: #909399;
  background: rgba(144, 147, 153, 0.12);
}

.status-tag {
  font-size: 12px;
  color: #909399;

  &.unread {
    color: #F56C6C;
  }
}

.time-text {
  font-size: 12px;
  color: #909399;
}

.notice-pagination {
  display: flex;
  justify-content: center;
  padding-top: 8px;
  border-top: 1px solid #EEF1F3;
}

.notice-detail-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 8px 8px 4px;
  min-width: 0;
}

.notice-detail-header {
  border-bottom: 1px solid #EEF1F3;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.notice-detail-title {
  font-size: 20px;
  font-weight: 600;
  color: #212121;
  margin-bottom: 10px;
  word-break: break-word;
}

.notice-detail-content {
  flex: 1;
  overflow-y: auto;
  font-size: 18px;
  line-height: 1.8;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
}

.notice-detail-actions {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #EEF1F3;
}
</style>
